import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';

import { savePrescriptionThroughOL } from './api';
import { trackError } from 'util/error';
import { SaveStatus, PrescriptionToSave, UpdatePrescriptionResponse, PrescriptionSavingStatus } from './types';

interface UseSavePrescriptionsParams {
  updatePrescriptionStatuses: (newStatus: PrescriptionSavingStatus[]) => void;
}

export const useSavePrescriptions = ({ updatePrescriptionStatuses }: UseSavePrescriptionsParams ) => {
  const { mutate } = useMutation<UpdatePrescriptionResponse, Error, PrescriptionToSave>({
    mutationFn: (prescription) => savePrescriptionThroughOL({ input: prescription }),
    onSuccess: (response, prescription) => updatePrescriptionStatuses([{
      prescriptionId: response.updatePrescriptionDetails.id,
      fieldId: prescription.fieldUuid,
      status: SaveStatus.SUCCEEDED,
    }]),
    onError: (error, prescription) => {
      updatePrescriptionStatuses([{
        prescriptionId: prescription.id,
        fieldId: prescription.fieldUuid,
        status: SaveStatus.FAILED,
        errorMessage: error.message,
      }]);
      trackError(new Error('Save planting prescription failed'), {
        fieldId: prescription.fieldUuid,
        errorMessage: error.message,
      });
    },
  });

  const initSavingPrescriptions = useCallback((prescriptions: PrescriptionToSave[]) => {
    const initialStatuses = prescriptions.map((prescription, index) => ({
      prescriptionId: prescription.id,
      fieldId: prescription.fieldUuid,
      status: SaveStatus.PENDING,
      id: index + 1,
    }));

    updatePrescriptionStatuses(initialStatuses);

    prescriptions.forEach((prescription) => mutate(prescription));
  },
  [mutate, updatePrescriptionStatuses],
  );

  return { initSavingPrescriptions };
};
